import React from 'react';
import { Link } from 'react-router-dom';
import './StaticLegalPages.css'; // Shared CSS for all legal text pages

const PrivacyPage = () => {
  return (
    <div className="static-page-container">
      <h1>Privacy Policy</h1>
      <p><strong>Last updated: January 20, 2026</strong></p>

      <div className="policy-binding-notice" style={{ 
        backgroundColor: '#f7f5f2', 
        padding: '15px', 
        borderLeft: '4px solid #8A181A',
        marginBottom: '20px',
        fontSize: '0.95rem'
      }}>
        <p>
          <strong>Notice:</strong> By creating an account, signing up, or using FullMockup, 
          you acknowledge that you have read, understood, and agree to be bound by this 
          Privacy Policy and our <Link to="/terms">Terms & Conditions</Link>.
        </p>
      </div>

      <h2>1. Introduction</h2>
      <p>
        Welcome to <strong>FullMockup</strong> ("we," "us," or "our"). We are committed 
        to protecting your personal information and your right to privacy. To learn 
        more about our mission, please see our <Link to="/about-us">About Us</Link> page.
      </p>
      <p>
        FullMockup provides a professional SaaS platform for designers and sellers 
        to create high-quality product mockups. We operate under strict data 
        minimization principles and comply with applicable regulations, including 
        the <strong>Information Technology Act, 2000 (India)</strong>.
      </p>

      <h2>2. The Data We Collect</h2>
      <p>We collect only the data necessary to provide and secure our Services.</p>
      <ul>
        <li>
          <strong>A. Personal Information:</strong> We collect your Name, Email, 
          and credentials. Passwords are never stored in plain text; they are 
          salted and hashed via our authentication provider (Supabase).
        </li>
        <li>
          <strong>B. User Content (Inputs):</strong> We collect the images you 
          upload, including Base Garment images and Design Assets (Logos/Artwork).
        </li>
        <li>
          <strong>C. Generated Content (Outputs):</strong> We store the final 
          mockup images produced by our proprietary engine.
        </li>
        <li>
          <strong>D. Payment Information:</strong> Transactions are processed by 
          <strong> Cashfree Payments</strong>. We do not store credit card 
          numbers, bank details, or UPI IDs on our servers.
        </li>
        <li>
          <strong>E. Technical Metadata:</strong> For security, DDoS protection, 
          and fraud prevention, we automatically log IP addresses, Browser Types, 
          and Device Information to verify transaction authenticity.
        </li>
      </ul>

      <h2>3. Our Technology & Data Sovereignty</h2>
      <p>
        Unlike many AI tools, FullMockup operates a <strong>Self-Hosted Proprietary 
        Engine</strong>.
      </p>
      <ul>
        <li>
          <strong>Private Processing:</strong> All image warping and physics 
          simulations occur locally on our private Virtual Private Servers (VPS).
        </li>
        <li>
          <strong>No Third-Party AI Sharing:</strong> Your images and designs 
          are <strong>never</strong> sent to third-party AI APIs (such as OpenAI 
          or Replicate). Your intellectual property never leaves our controlled 
          infrastructure during the generation process.
        </li>
      </ul>

      <h2>4. Data Retention & Auto-Deletion Policy</h2>
      <p>We implement automated lifecycle rules to ensure user privacy:</p>
      <ul>
        <li>
          <strong>Starter (Free) Users:</strong> Input images and generated 
          mockups are <strong>automatically and permanently deleted</strong> 24 
          hours after generation.
        </li>
        <li>
          <strong>Paid Subscribers:</strong> Assets are stored securely for the 
          duration of an active subscription. Upon cancellation, data is 
          retained for a 30-day grace period before potential deletion.
        </li>
        <li>
          <strong>The "Recycle Bin":</strong> Manually deleted images enter a 
          "Soft Delete" state for 24 hours. After this window, the files are 
          permanently purged from our storage.
        </li>
      </ul>

      <h2>5. How We Share Your Data</h2>
      <p>
        We do not sell your data. We only share information with infrastructure 
        providers essential to our Service:
      </p>
      <ul>
        <li><strong>Supabase:</strong> For secure authentication and database management.</li>
        <li><strong>Cloudflare:</strong> For secure, high-performance file storage.</li>
        <li><strong>Cashfree:</strong> For processing secure, compliant payments.</li>
      </ul>

      <h2>6. Security of Your Data</h2>
      <p>
        We use SSL/TLS (HTTPS) encryption for all data in transit. Data at rest 
        in our storage containers is encrypted. Access to backend infrastructure 
        is strictly limited to authorized personnel for maintenance purposes only.
      </p>

      <h2>7. Cookies</h2>
      <p>
        We use essential cookies to maintain your authentication state. We do 
        not use third-party tracking cookies for advertising purposes.
      </p>

      <h2>8. Your Data Rights</h2>
      <p>
        In accordance with the Information Technology Act, 2000, you have the 
        right to review, correct, or request the deletion of your personal data. 
        You may withdraw consent at any time by initiating account deletion via 
        your Dashboard, which may result in immediate termination of Service access.
      </p>

      <h2>9. Children’s Privacy</h2>
      <p>
        FullMockup is intended for professional use. We do not knowingly collect 
        information from anyone under the age of 18. If we discover data 
        collected from a minor, it will be deleted immediately.
      </p>

      <h2>10. Governing Law & Jurisdiction</h2>
      <p>
        This Privacy Policy is governed by the <strong>laws of India</strong>. 
        Any disputes arising from this policy shall be subject to the exclusive 
        jurisdiction of the courts located in <strong>New Delhi</strong>, India.
      </p>

      <h2>11. Contact Us</h2>
      <p>
        For questions regarding this policy or our data practices, please 
        reach out to our compliance team:
      </p>
      <div className="contact-details-box" style={{ 
        border: '1px solid #5F4729', 
        padding: '20px', 
        borderRadius: '8px',
        margin: '15px 0',
        lineHeight: '1.6',
        backgroundColor: '#ffffff'
      }}>
        <p><strong>Support Email:</strong> support@fullmockup.com</p>
        <p><strong>Phone:</strong> +91 9560695885</p>
        <p><strong>Address:</strong> R-53A, Sir Syed Road, Jamia Nagar, Okhla, New Delhi, India.</p>
      </div>
      <p>
        Alternatively, you may use our <Link to="/contact-us">Official Contact Form</Link>.
      </p>
    </div>
  );
};

export default PrivacyPage;